import React from "react";
import { apiDelete, apiPost, apiPut, useGet } from "../../hooks/useApi.js";
import { formatHours, hoursToMinutes, minutesToHours } from "../../lib/format.js";
type Type = {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  leadTimeMinutes: number;
  horizonDays: number;
  active: boolean;
};
const blank = {
  name: "",
  description: "",
  durationMinutes: 60,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  leadTimeMinutes: 60,
  horizonDays: 30,
  active: true,
};
export function AppointmentTypesPage() {
  const q = useGet<{ items: Type[] }>("/api/v1/appointment-types");
  const [form, setForm] = React.useState(blank);
  const [editing, setEditing] = React.useState<string | null>(null);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      editing
        ? await apiPut("/api/v1/appointment-types/" + editing, form)
        : await apiPost("/api/v1/appointment-types", form);
      setForm(blank);
      setEditing(null);
      q.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save");
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Appointment types</h1>
        <p className="text-sm text-[hsl(var(--content-subtle))]">
          Control the services customers can schedule and their timing rules.
        </p>
      </div>
      <form
        onSubmit={save}
        className="rounded-xl border p-5 grid gap-4 md:grid-cols-2"
        aria-label="Appointment type form"
      >
        <label className="text-sm">
          Name
          <input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1 w-full rounded border bg-transparent p-2"
          />
        </label>
        <label className="text-sm">
          Duration (hours)
          <input
            type="number"
            min="0.25"
            step="0.25"
            required
            value={minutesToHours(form.durationMinutes)}
            onChange={(e) => {
              try { setForm({ ...form, durationMinutes: hoursToMinutes(Number(e.target.value)) }); setError(''); }
              catch { setError('Duration must use 0.25-hour increments.'); }
            }}
            className="mt-1 w-full rounded border bg-transparent p-2"
          />
        </label>
        <label className="text-sm md:col-span-2">
          Description
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1 w-full rounded border bg-transparent p-2"
          />
        </label>
        {error && (
          <p role="alert" className="text-red-600 md:col-span-2">
            {error}
          </p>
        )}
        <div className="flex gap-2 md:col-span-2">
          <button
            disabled={saving}
            className="rounded bg-brand-600 px-4 py-2 text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : editing ? "Update type" : "Add type"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(blank);
              }}
              className="rounded border px-4 py-2"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
      {q.loading ? (
        <p role="status">Loading appointment types…</p>
      ) : q.error ? (
        <div role="alert">
          <p>Couldn’t load appointment types: {q.error}</p>
          <button onClick={q.refetch} className="underline">
            Try again
          </button>
        </div>
      ) : !q.data?.items.length ? (
        <div className="rounded-xl border border-dashed p-10 text-center">
          <h2 className="font-medium">No appointment types yet</h2>
          <p className="text-sm text-[hsl(var(--content-subtle))]">
            Add the first type to create secure booking links.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {q.data.items.map((x) => (
            <li key={x.id} className="rounded-xl border p-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="font-medium">{x.name}</h2>
                <p className="text-sm text-[hsl(var(--content-subtle))]">
                  {x.description || "No description"} · {formatHours(minutesToHours(x.durationMinutes))} ·{" "}
                  {x.active ? "Active" : "Inactive"}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  className="rounded border px-3 py-1.5"
                  onClick={() => {
                    setEditing(x.id);
                    setForm({
                      name: x.name,
                      description: x.description ?? "",
                      durationMinutes: x.durationMinutes,
                      bufferBeforeMinutes: x.bufferBeforeMinutes,
                      bufferAfterMinutes: x.bufferAfterMinutes,
                      leadTimeMinutes: x.leadTimeMinutes,
                      horizonDays: x.horizonDays,
                      active: x.active,
                    });
                  }}
                >
                  Edit
                </button>
                <button
                  className="rounded border px-3 py-1.5 text-red-600"
                  onClick={async () => {
                    if (confirm("Delete this appointment type?")) {
                      try {
                        await apiDelete("/api/v1/appointment-types/" + x.id);
                        q.refetch();
                      } catch (e) {
                        setError(e instanceof Error ? e.message : "Delete failed");
                      }
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
